import { useDebounce } from '@workspace/utils';
import { useEffect, useMemo, useState } from 'react';
import type { Path } from 'react-hook-form';
import { Controller, get, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { FieldCombobox, FieldInput, type ComboboxOption } from '.';
import { useAddress, useTownByZipCode } from '@workspace/api';
import { Field, Label, SelectNative, TextError } from '../form';

export interface AdressesProps<TFormValues extends Record<string, any> = any> {
  nameCP: Path<TFormValues> | string;
  nameVille: Path<TFormValues> | string;
  nameAdresse: Path<TFormValues> | string;
  nameComplement: Path<TFormValues> | string;
  disabled?: boolean;
  required?: boolean;
  validationCP?: Record<string, any>;
  /** Affiche le lien "Je ne trouve pas mon adresse" pour basculer en saisie manuelle */
  allowManualEntry?: boolean;
}

interface AddressResult {
  label: string;
  line1: string;
  line2: string;
  adresse: string;
  complement?: string;
  cp: string;
  ville: string;
}

const ADDRESS_CONFIG = {
  minQueryLength: 5,
  debounceDelay: 200,
  placeholder: 'Tapez au moins 5 caractères pour rechercher',
  emptyMessage: 'Aucune adresse trouvée.',
  loadingMessage: "Recherche d'adresses en cours...",
  manualEntryLabel: 'Je ne trouve pas mon adresse',
  searchEntryLabel: 'Rechercher mon adresse',
  postalCodeLength: 5,
  townPlaceholder: 'Sélectionnez une ville',
} as const;

export function FieldAdresses({
  nameCP,
  nameVille,
  nameAdresse,
  nameComplement,
  disabled = false,
  required = true,
  validationCP,
  allowManualEntry = false,
}: AdressesProps) {
  const methods = useFormContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const debouncedQuery = useDebounce(searchQuery, ADDRESS_CONFIG.debounceDelay);
  const addressQuery = useAddress(debouncedQuery);

  // Valeurs courantes des champs
  const currentAdresse = methods.watch(nameAdresse);
  const currentCP = methods.watch(nameCP);
  const currentVille = methods.watch(nameVille);

  // En saisie manuelle, le code postal pilote la recherche des villes
  // (la requête n'est pas déclenchée tant que l'on reste en mode recherche d'adresse)
  const townQuery = useTownByZipCode(manualMode ? currentCP : '');
  const townOptions = useMemo(
    () => (townQuery.data?.data ?? []).map((town) => ({ value: town.toUpperCase(), label: town.toUpperCase() })),
    [townQuery.data],
  );
  // Repli en saisie libre si l'API des villes échoue ou ne connaît pas ce code postal
  const townLookupFailed = townQuery.isError || (townQuery.isSuccess && townOptions.length === 0);
  const showTownSelect = manualMode && !townLookupFailed;

  // Bascule en mode manuel si l'API échoue
  useEffect(() => {
    if (addressQuery.isError) setManualMode(true);
  }, [addressQuery.isError]);

  // Pré-sélectionne la première ville si la ville courante n'appartient pas au code postal saisi
  useEffect(() => {
    if (!showTownSelect || townOptions.length === 0) return;
    const ville = methods.getValues(nameVille);
    if (!ville || !townOptions.some((option) => option.value === ville)) {
      methods.setValue(nameVille, townOptions[0].value, { shouldValidate: true });
    }
  }, [showTownSelect, townOptions, methods, nameVille]);

  // En saisie manuelle, une ville héritée d'un autre code postal n'a plus de sens :
  // on la vide pendant la frappe du code postal et quand le code postal saisi est inconnu
  useEffect(() => {
    if (!manualMode) return;
    const cpIncomplete = String(currentCP ?? '').length < ADDRESS_CONFIG.postalCodeLength;
    const cpUnknown = townQuery.isSuccess && townOptions.length === 0;
    if ((cpIncomplete || cpUnknown) && methods.getValues(nameVille)) {
      methods.setValue(nameVille, '', { shouldValidate: false });
    }
  }, [manualMode, currentCP, townQuery.isSuccess, townOptions, methods, nameVille]);

  // Le retour à la recherche n'a de sens que si l'API répond
  const canToggleManualMode = allowManualEntry && !addressQuery.isError && !disabled;

  // Options de recherche
  const options: ComboboxOption<AddressResult>[] = useMemo(() => {
    if (!Array.isArray(addressQuery.data) || addressQuery.isError) return [];

    return addressQuery.data.map((item) => ({
      value: `${item.additionalInfo_1} ${item.additionalInfo_2}`.replace(/\s+/g, ' ').trim(),
      label: `${item.additionalInfo_1} ${item.additionalInfo_2}`.replace(/\s+/g, ' ').trim(),
      data: {
        label: `${item.additionalInfo_1} ${item.additionalInfo_2}`.replace(/\s+/g, ' ').trim(),
        line1: item.additionalInfo_1 ?? '',
        line2: item.additionalInfo_2 ?? '',
        adresse: item.additionalInfo_1 ?? '',
        complement: '',
        cp: item.postalCode ?? '',
        ville: item.locality ?? '',
      },
    }));
  }, [addressQuery.data, addressQuery.isError]);

  // Handler de sélection : remplit tous les champs
  const handleAddressSelection = (option: ComboboxOption<AddressResult> | null) => {
    if (!option?.data) return;
    const { adresse, complement, cp, ville } = option.data;

    methods.setValue(nameAdresse, adresse.toUpperCase(), { shouldValidate: true });
    methods.setValue(nameCP, cp, { shouldValidate: true });
    methods.setValue(nameVille, ville.toUpperCase(), { shouldValidate: true });
    if (complement) methods.setValue(nameComplement, complement.toUpperCase());
  };

  // Init search avec valeurs existantes
  useEffect(() => {
    if (currentAdresse && currentCP && currentVille && !searchQuery) {
      setSearchQuery(`${currentAdresse} ${currentCP} ${currentVille}`);
    }
  }, [currentAdresse, currentCP, currentVille, searchQuery]);

  return (
    <>
      {!manualMode && (
        <FieldCombobox
          label="Rechercher une adresse"
          name={`${nameAdresse}-search`}
          options={options}
          required={false}
          disabled={disabled}
          placeholder={ADDRESS_CONFIG.placeholder}
          loading={addressQuery.isFetching}
          loadingMessage={ADDRESS_CONFIG.loadingMessage}
          emptyMessage={ADDRESS_CONFIG.emptyMessage}
          minQueryLength={ADDRESS_CONFIG.minQueryLength}
          filterOptions={false}
          onInputChange={setSearchQuery}
          onChange={handleAddressSelection}
          renderOption={(option) => (
            <div className="flex flex-col">
              <span className="font-medium text-foreground">{option.data?.line1}</span>
              <span className="text-xs text-muted-foreground">{option.data?.line2}</span>
            </div>
          )}
        />
      )}

      {canToggleManualMode && (
        <button
          type="button"
          onClick={() => setManualMode((mode) => !mode)}
          className="self-start text-sm font-medium text-primary hover:underline"
        >
          {manualMode ? ADDRESS_CONFIG.searchEntryLabel : ADDRESS_CONFIG.manualEntryLabel}
        </button>
      )}

      {/* Champs décomposés avec validation */}
      <FieldInput
        label="Adresse"
        name={nameAdresse}
        disabled={!manualMode}
        required={required}
        placeholder="Ex: 10 rue de la République"
        normalize="uppercase"
      />
      <div className="grid grid-cols-2 gap-4 w-full">
        <FieldInput
          label="Code postal"
          name={nameCP}
          disabled={!manualMode}
          required={required}
          placeholder="Ex: 75001"
          validation={validationCP}
          inputMode="numeric"
          maxLength={ADDRESS_CONFIG.postalCodeLength}
          loading={manualMode && townQuery.isFetching}
        />
        {showTownSelect ? (
          <Field>
            <Label required={required}>Ville</Label>
            <Controller
              name={nameVille}
              control={methods.control}
              rules={{ required: required ? 'Ce champs est requis' : false }}
              render={({ field }) => (
                <SelectNative
                  {...field}
                  value={field.value || ''}
                  options={townOptions}
                  placeholder={ADDRESS_CONFIG.townPlaceholder}
                  disabled={disabled || methods.formState.isSubmitting || townOptions.length === 0}
                  error={get(methods.formState.errors, nameVille)}
                />
              )}
            />
            <ErrorMessage
              errors={methods.formState.errors}
              name={nameVille}
              render={({ message }) => <TextError>{message}</TextError>}
            />
          </Field>
        ) : (
          <FieldInput
            label="Ville"
            name={nameVille}
            disabled={!manualMode}
            required={required}
            placeholder="Ex: Paris"
            normalize="uppercase"
          />
        )}
      </div>
      <FieldInput
        label="Complément d'adresse"
        name={nameComplement}
        required={false}
        placeholder="Ex: Appartement 3B"
        normalize="uppercase"
      />
    </>
  );
}
